import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { tenantsService } from '../services/tenants.service';
import { User } from '../types/auth';
import { TenantDetail } from '../types/tenant';
import { colocService, Coloc, getSelectedColoc, setSelectedColoc } from '../services/coloc.service';
import { getErrorMessage, isHttpError } from '../utils/error';
import '../styles/TenantLayout.css';

interface TenantLayoutProps {
  children: React.ReactNode;
  /** Aperçu du propriétaire (« Voir son espace ») : tout vient de ce dossier,
   *  tel que le locataire le reçoit ; rien n'est chargé pour le compte connecté. */
  apercu?: TenantDetail;
}

const TenantLayout: React.FC<TenantLayoutProps> = ({ children, apercu }) => {
  const [user, setUser] = useState<User | null>(null);
  const [colocs, setColocs] = useState<Coloc[]>([]);
  const [selectedColoc, setCurrentColoc] = useState<Coloc | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Message du refus d'accès (403 sur /tenants/me). Le serveur en donne la
  // cause, prête à afficher : avant l'entrée, location terminée, ou accès coupé
  // par le propriétaire.
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (apercu) return;
    const loadUserAndColocs = async () => {
      setIsLoading(true);
      try {
        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          navigate('/signin');
          return;
        }
        setUser(currentUser.user);

        // Garde d'accès locataire : un espace fermé doit afficher sa cause,
        // pas une page vide. 404 (pas encore de bail) et erreurs
        // réseau laissent passer (fail-open, comme AccessGate côté owner).
        try {
          await tenantsService.getMyDossier();
        } catch (err) {
          if (isHttpError(err, 403)) {
            setAccessDeniedMessage(getErrorMessage(
              err,
              // Repli neutre : « désactivé par le propriétaire » était faux
              // avant l'entrée et après la fin de la location.
              "Votre espace locataire n'est pas accessible pour le moment.",
            ));
          }
        }

        const userColocs = await colocService.getColocs();
        setColocs(userColocs);

        const storedColoc = getSelectedColoc();
        if (storedColoc) {
          const exists = userColocs.some(c => c.id === storedColoc.id);
          if (exists) {
            setCurrentColoc(storedColoc);
          } else if (userColocs.length > 0) {
            setCurrentColoc(userColocs[0]);
            setSelectedColoc(userColocs[0]);
          }
        } else if (userColocs.length > 0) {
          setCurrentColoc(userColocs[0]);
          setSelectedColoc(userColocs[0]);
        }
      } catch {
        // silent
      } finally {
        setIsLoading(false);
      }
    };

    loadUserAndColocs();
  }, [navigate, apercu]);

  const handleLogout = () => {
    authService.logout();
    navigate('/signin');
  };

  const handleColocChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const colocId = e.target.value;
    const coloc = colocs.find(c => c.id === colocId);
    if (coloc) {
      setCurrentColoc(coloc);
      setSelectedColoc(coloc);
    }
  };

  if (isLoading && !apercu) {
    return <div className="tenant-loading">Chargement...</div>;
  }

  // L'aperçu montre l'espace même fermé : le bailleur veut voir ce qu'il a coupé.
  // Le message que reçoit le locataire passe alors dans le bandeau.
  const refus = apercu ? null : accessDeniedMessage;
  const personne = apercu ?? user;
  const base = apercu ? `/tenants/${apercu.id}/espace` : '/tenant';

  return (
    <div className="tenant-layout">
      {apercu && (
        <div className="tenant-apercu-bandeau" role="status">
          {apercu.motifAccesFerme ? (
            <span>
              Aperçu de l&apos;espace de {apercu.firstName} {apercu.lastName}.{' '}
              <strong>Son accès est fermé</strong> : ce locataire ne voit aujourd&apos;hui
              que ce message : « {apercu.motifAccesFerme} »
            </span>
          ) : (
            <span>
              Aperçu de l&apos;espace de {apercu.firstName} {apercu.lastName},
              tel que ce locataire le voit.
            </span>
          )}
          <Link to={`/tenants/${apercu.id}`}>Revenir à sa fiche</Link>
        </div>
      )}
      <header className="tenant-header">
        <div className="tenant-header-left">
          <h1 className="tenant-logo">Coloc App</h1>
          {!refus && apercu?.colocName && (
            <span className="tenant-coloc-name">{apercu.colocName}</span>
          )}
          {!apercu && !accessDeniedMessage && colocs.length > 1 && (
            <select
              value={selectedColoc?.id || ''}
              onChange={handleColocChange}
              className="tenant-coloc-select"
            >
              {colocs.map(coloc => (
                <option key={coloc.id} value={coloc.id}>
                  {coloc.name}
                </option>
              ))}
            </select>
          )}
          {!apercu && !accessDeniedMessage && colocs.length === 1 && selectedColoc && (
            <span className="tenant-coloc-name">{selectedColoc.name}</span>
          )}
        </div>
        <div className="tenant-header-right">
          <span className="tenant-user-name">{personne?.firstName} {personne?.lastName}</span>
          {!apercu && (
            <button onClick={handleLogout} className="tenant-logout-btn">
              Déconnexion
            </button>
          )}
        </div>
      </header>

      {!refus && (
        <nav className="tenant-nav">
          <NavLink to={`${base}/dashboard`} className={({ isActive }) => `tenant-nav-link${isActive ? ' active' : ''}`}>
            Mes charges
          </NavLink>
          <NavLink to={`${base}/dossier`} className={({ isActive }) => `tenant-nav-link${isActive ? ' active' : ''}`}>
            Mon dossier
          </NavLink>
        </nav>
      )}

      <main className="tenant-main">
        {refus ? (
          <div className="tenant-access-denied">
            <h2>Espace indisponible</h2>
            <p>{refus}</p>
          </div>
        ) : (
          children
        )}
      </main>
    </div>
  );
};

export default TenantLayout;
